'use client';

import { IoMdSearch } from '@react-icons/all-files/io/IoMdSearch';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FormEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { ISearchableWidget, searchWidgets } from '@/helpers/widgetSearch';
import { trackEvent } from '@/lib/track';
import { CustomLink } from '../link/link.comp';
import { popularWidgets } from './popularWidgets.data';
import { StyledPopularWidgets } from './popularWidgets.styles';

const MAX_SUGGESTIONS = 8;

interface IPopularWidgetsProps {
	searchWidgets: ISearchableWidget[];
}

export const PopularWidgets = ({
	searchWidgets: widgets,
}: IPopularWidgetsProps) => {
	const router = useRouter();
	const [query, setQuery] = useState('');
	const [suggestOpen, setSuggestOpen] = useState(false);
	const [activeSuggestion, setActiveSuggestion] = useState(-1);

	const suggestions = useMemo(
		() =>
			query.trim().length > 1
				? searchWidgets(widgets, query).slice(0, MAX_SUGGESTIONS)
				: [],
		[widgets, query]
	);
	const showSuggestions = suggestOpen && suggestions.length > 0;

	// Keep the highlighted suggestion visible when arrowing through the list.
	useEffect(() => {
		if (activeSuggestion < 0) return;
		document
			.getElementById(`homepage-widget-suggestion-${activeSuggestion}`)
			?.scrollIntoView({ block: 'nearest' });
	}, [activeSuggestion]);

	function openSuggestion(widget: ISearchableWidget) {
		trackEvent('homepage_widget_search_suggestion', {
			query: query.trim(),
			widget: widget.name,
		});
		setSuggestOpen(false);
		router.push(widget.href);
	}

	function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
		if (!showSuggestions) return;
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActiveSuggestion((i) => (i + 1) % suggestions.length);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
		} else if (e.key === 'Enter' && activeSuggestion >= 0) {
			e.preventDefault();
			openSuggestion(suggestions[activeSuggestion]);
		} else if (e.key === 'Escape') {
			setSuggestOpen(false);
		}
	}

	// /widgets reads ?search= and pre-fills its own catalog search with it.
	// The form still has a real action so it works before hydration too.
	function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSuggestOpen(false);
		const search = query.trim();
		trackEvent('homepage_widget_search', { query: search });
		router.push(
			search ? `/widgets?search=${encodeURIComponent(search)}` : '/widgets'
		);
	}

	return (
		<StyledPopularWidgets>
			<div className="content">
				<h2>Popular Widgets</h2>
				<p className="subtitle">
					Jump straight to the widget you need, ready to customize and embed
					on any website in minutes.
				</p>
				<form
					className="widget-search"
					action="/widgets"
					method="get"
					role="search"
					onSubmit={handleSubmit}
				>
					<IoMdSearch className="search-icon" aria-hidden="true" />
					<input
						type="search"
						name="search"
						role="combobox"
						aria-label="Search widgets"
						aria-autocomplete="list"
						aria-expanded={showSuggestions}
						aria-controls="homepage-widget-suggestions"
						aria-activedescendant={
							showSuggestions && activeSuggestion >= 0
								? `homepage-widget-suggestion-${activeSuggestion}`
								: undefined
						}
						autoComplete="off"
						placeholder="Search widgets..."
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setSuggestOpen(true);
							setActiveSuggestion(-1);
						}}
						onFocus={() => setSuggestOpen(true)}
						onBlur={() => setSuggestOpen(false)}
						onKeyDown={handleKeyDown}
					/>
					<button type="submit">Search</button>
					{showSuggestions && (
						<ul
							className="suggestions"
							id="homepage-widget-suggestions"
							role="listbox"
							// Keeps focus in the input, so the blur above does not
							// close the list before a click lands.
							onMouseDown={(e) => e.preventDefault()}
						>
							{suggestions.map((widget, i) => (
								<li
									key={widget.href}
									id={`homepage-widget-suggestion-${i}`}
									role="option"
									aria-selected={i === activeSuggestion}
								>
									<Link
										href={widget.href}
										className={i === activeSuggestion ? 'active' : ''}
										onClick={(e) => {
											e.preventDefault();
											openSuggestion(widget);
										}}
										onMouseEnter={() => setActiveSuggestion(i)}
									>
										<Image src={widget.logo} alt="" width={28} height={28} />
										<span>
											<strong>{widget.name}</strong>
											{widget.categories[0] && (
												<small>{widget.categories[0]}</small>
											)}
										</span>
									</Link>
								</li>
							))}
						</ul>
					)}
				</form>
				<ul className="featured-grid">
					{popularWidgets.map((widget) => (
						<li key={widget.slug}>
							<Link
								className="widget-card"
								href={`/widgets/${widget.slug}`}
								title={`${widget.name} widget`}
							>
								<span className="widget-icon">
									<Image
										src={widget.logo}
										alt=""
										width={48}
										height={48}
										loading="lazy"
									/>
								</span>
								<span className="widget-name">{widget.name}</span>
								<span className="widget-category">{widget.category}</span>
							</Link>
						</li>
					))}
				</ul>
				<div className="browse-all">
					<CustomLink
						variant="gradient"
						href="/widgets"
						title="Browse all widgets"
					>
						Browse All 220+ Widgets
					</CustomLink>
				</div>
			</div>
		</StyledPopularWidgets>
	);
};
